<template>
  <transition name="osk-fade">
    <div
      v-if="enabled"
      class="osk"
      :style="positionStyle"
      role="dialog"
      :aria-label="$t('Components.OnScreenKeyboard.dialogLabel')"
    >
      <!-- Drag handle / header -->
      <div class="osk__header" @mousedown="startDrag" @touchstart="startDrag">
        <div class="osk__title">
          <i class="far fa-keyboard"></i>
          {{ $t('Components.OnScreenKeyboard.title') }}
          <span class="osk__focus-hint" v-if="focusedField">
            {{
              $t('Components.OnScreenKeyboard.typingInto', {
                field: focusedField
              })
            }}
          </span>
        </div>
        <div class="osk__controls" @mousedown.prevent>
          <button
            class="osk__btn osk__btn--small"
            @click="toggleCaps"
            @mousedown.prevent
            :class="{ 'osk__btn--active': caps }"
            type="button"
            :aria-label="$t('Components.OnScreenKeyboard.toggleCaps')"
          >
            ABC
          </button>
          <button
            class="osk__btn osk__btn--small"
            @click="toggleNumbers"
            @mousedown.prevent
            :class="{ 'osk__btn--active': showNumbers }"
            type="button"
            :aria-label="$t('Components.OnScreenKeyboard.toggleNumbers')"
          >
            123
          </button>
          <button
            class="osk__btn osk__btn--close"
            @click="close"
            @mousedown.prevent
            type="button"
            :aria-label="$t('Components.OnScreenKeyboard.closeKeyboard')"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Keys -->
      <div class="osk__rows" @mousedown.prevent>
        <div v-for="(row, r) in activeRows" :key="r" class="osk__row">
          <button
            v-for="(key, k) in row"
            :key="r + '-' + k"
            class="osk__key"
            :class="keyClass(key)"
            @click="pressKey(key)"
            type="button"
            :aria-label="keyAriaLabel(key)"
          >
            {{ displayKeyLabel(key) }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const LETTERS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['{shift}', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '{backspace}'],
  ['{numbers}', '{space}', '{left}', '{right}', '{enter}']
];

const NUMBERS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['@', '#', '$', '%', '&', '*', '(', ')', '-', '_'],
  ['+', '=', '/', ':', ';', "'", '"', ',', '.', '?'],
  ['{abc}', '{space}', '{backspace}', '{enter}']
];

export default {
  name: 'OnScreenKeyboard',
  data() {
    return {
      caps: false,
      showNumbers: false,
      position: { x: null, y: null },
      dragging: false,
      dragOffset: { x: 0, y: 0 },
      lastFocused: null
    };
  },
  computed: {
    enabled() {
      return !!this.$store.getters['Accessibility/getFeatures']
        .onScreenKeyboard;
    },
    activeRows() {
      return this.showNumbers ? NUMBERS : LETTERS;
    },
    focusedField() {
      if (!this.lastFocused) return null;
      const el = this.lastFocused;
      if (el.getAttribute) {
        return (
          el.getAttribute('aria-label') ||
          el.getAttribute('placeholder') ||
          el.getAttribute('name') ||
          el.tagName.toLowerCase()
        );
      }
      return null;
    },
    positionStyle() {
      if (this.position.x === null) return {};
      return {
        left: this.position.x + 'px',
        bottom: 'auto',
        top: this.position.y + 'px',
        right: 'auto'
      };
    }
  },
  watch: {
    enabled(v) {
      if (v) this.bindFocusTracker();
      else this.unbindFocusTracker();
    }
  },
  mounted() {
    if (this.enabled) this.bindFocusTracker();
  },
  beforeDestroy() {
    this.unbindFocusTracker();
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.onDragMove);
    document.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    close() {
      this.$store.dispatch('Accessibility/setFeature', {
        key: 'onScreenKeyboard',
        value: false
      });
    },
    toggleCaps() {
      this.caps = !this.caps;
    },
    toggleNumbers() {
      this.showNumbers = !this.showNumbers;
    },
    bindFocusTracker() {
      // Remember the last focused text input so clicking keys types
      // into the right field even when the OSK itself is focused.
      if (this._focusHandler) return;
      this._focusHandler = (e) => {
        const t = e.target;
        if (!t) return;
        if (
          t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable
        ) {
          // Ignore focus inside the OSK itself
          if (t.closest('.osk')) return;
          this.lastFocused = t;
        }
      };
      document.addEventListener('focusin', this._focusHandler, true);
    },
    unbindFocusTracker() {
      if (this._focusHandler) {
        document.removeEventListener('focusin', this._focusHandler, true);
        this._focusHandler = null;
      }
    },
    keyClass(key) {
      if (!key.startsWith('{')) return '';
      return {
        'osk__key--wide': [
          '{space}',
          '{enter}',
          '{backspace}',
          '{shift}',
          '{numbers}',
          '{abc}'
        ].includes(key),
        'osk__key--xwide': key === '{space}',
        'osk__key--action': key !== '{space}' && key.startsWith('{'),
        'osk__key--active': key === '{shift}' && this.caps
      };
    },
    displayKeyLabel(key) {
      if (key === '{space}')
        return '␣ ' + this.$t('Components.OnScreenKeyboard.space');
      if (key === '{enter}')
        return '↵ ' + this.$t('Components.OnScreenKeyboard.enter');
      if (key === '{backspace}') return '⌫';
      if (key === '{shift}') return this.caps ? 'ABC' : 'abc';
      if (key === '{numbers}') return '123';
      if (key === '{abc}') return 'ABC';
      if (key === '{left}') return '◀';
      if (key === '{right}') return '▶';
      return this.caps && !this.showNumbers ? key.toUpperCase() : key;
    },
    keyAriaLabel(key) {
      if (key === '{space}')
        return this.$t('Components.OnScreenKeyboard.space');
      if (key === '{enter}')
        return this.$t('Components.OnScreenKeyboard.enter');
      if (key === '{backspace}')
        return this.$t('Components.OnScreenKeyboard.backspace');
      if (key === '{shift}')
        return this.$t('Components.OnScreenKeyboard.shift');
      if (key === '{numbers}')
        return this.$t('Components.OnScreenKeyboard.showNumbers');
      if (key === '{abc}')
        return this.$t('Components.OnScreenKeyboard.showLetters');
      if (key === '{left}')
        return this.$t('Components.OnScreenKeyboard.cursorLeft');
      if (key === '{right}')
        return this.$t('Components.OnScreenKeyboard.cursorRight');
      return this.$t('Components.OnScreenKeyboard.letter', { key });
    },
    pressKey(key) {
      const el = this.lastFocused;
      if (!el || !el.isConnected) return;

      // Action keys
      if (key === '{shift}') return this.toggleCaps();
      if (key === '{numbers}') return (this.showNumbers = true);
      if (key === '{abc}') return (this.showNumbers = false);
      if (key === '{backspace}') return this.applyBackspace(el);
      if (key === '{enter}') return this.applyEnter(el);
      if (key === '{space}') return this.insertAtCursor(el, ' ');
      if (key === '{left}') return this.moveCaret(el, -1);
      if (key === '{right}') return this.moveCaret(el, 1);

      // Plain character
      const char = this.caps && !this.showNumbers ? key.toUpperCase() : key;
      this.insertAtCursor(el, char);
      // Single-shot shift: reset caps after the next character
      // (Mimics most virtual keyboards)
    },
    insertAtCursor(el, text) {
      el.focus();
      if (el.isContentEditable) {
        document.execCommand('insertText', false, text);
        return;
      }
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      el.value = el.value.slice(0, start) + text + el.value.slice(end);
      el.selectionStart = el.selectionEnd = start + text.length;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    },
    applyBackspace(el) {
      el.focus();
      if (el.isContentEditable) {
        document.execCommand('delete');
        return;
      }
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      if (start === end && start === 0) return;
      if (start === end) {
        el.value = el.value.slice(0, start - 1) + el.value.slice(end);
        el.selectionStart = el.selectionEnd = start - 1;
      } else {
        el.value = el.value.slice(0, start) + el.value.slice(end);
        el.selectionStart = el.selectionEnd = start;
      }
      el.dispatchEvent(new Event('input', { bubbles: true }));
    },
    applyEnter(el) {
      el.focus();
      // Textarea → newline; input → try to submit its form
      if (el.tagName === 'TEXTAREA' || el.isContentEditable) {
        this.insertAtCursor(el, '\n');
        return;
      }
      const form = el.form || el.closest('form');
      if (form && form.requestSubmit) form.requestSubmit();
      else if (form)
        form.dispatchEvent(
          new Event('submit', { bubbles: true, cancelable: true })
        );
    },
    moveCaret(el, delta) {
      el.focus();
      if (el.selectionStart != null) {
        const pos = Math.max(
          0,
          Math.min(el.value.length, (el.selectionStart || 0) + delta)
        );
        el.selectionStart = el.selectionEnd = pos;
      }
    },
    startDrag(e) {
      const evt = e.touches ? e.touches[0] : e;
      const rect = this.$el.getBoundingClientRect();
      // Snap the OSK's internal position to its current visual spot on first drag
      if (this.position.x === null) {
        this.position = { x: rect.left, y: rect.top };
      }
      this.dragging = true;
      this.dragOffset = {
        x: evt.clientX - this.position.x,
        y: evt.clientY - this.position.y
      };
      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.onDragMove);
      document.addEventListener('touchend', this.stopDrag);
    },
    onDragMove(e) {
      if (!this.dragging) return;
      const evt = e.touches ? e.touches[0] : e;
      this.position = {
        x: Math.max(
          8,
          Math.min(window.innerWidth - 100, evt.clientX - this.dragOffset.x)
        ),
        y: Math.max(
          8,
          Math.min(window.innerHeight - 60, evt.clientY - this.dragOffset.y)
        )
      };
    },
    stopDrag() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.onDragMove);
      document.removeEventListener('touchend', this.stopDrag);
    }
  }
};
</script>

<style scoped lang="scss">
.osk {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 640px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  padding: 12px;
  z-index: 2147483646; /* Always on top — above any modal backdrop */
  font-family: 'Poppins', sans-serif;
  user-select: none;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px 10px;
    border-bottom: 1px solid #f3f4f6;
    margin-bottom: 10px;
    cursor: move;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: var(--color-primary, #047873);
    }
  }

  &__focus-hint {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__controls {
    display: flex;
    gap: 6px;
  }

  &__btn {
    height: 30px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1.5px solid #e5e7eb;
    background: #ffffff;
    color: #111827;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 700;
    transition: all 0.12s;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      background: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
      color: #ffffff;
    }

    &--small {
      min-width: 44px;
    }

    &--close {
      width: 30px;
      padding: 0;
      background: #f3f4f6;
      border-color: #e5e7eb;

      &:hover {
        background: #fee2e2;
        color: #dc2626;
        border-color: #fecaca;
      }
    }
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__row {
    display: flex;
    gap: 6px;
    justify-content: center;
  }

  &__key {
    flex: 1;
    min-width: 36px;
    height: 44px;
    border-radius: 8px;
    border: 1.5px solid #d1d5db;
    background: #ffffff;
    color: #111827;
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.1s;

    &:hover {
      background: #e6f5f4;
      border-color: var(--color-primary, #047873);
    }
    &:active {
      background: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
      color: #ffffff;
      transform: scale(0.95);
    }

    &--wide {
      flex: 1.6;
      font-size: 12px;
    }
    &--xwide {
      flex: 4;
      font-size: 12px;
    }
    &--action {
      background: #f3f4f6;
      color: #374151;
    }
    &--active {
      background: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
      color: #ffffff;
    }
  }
}

.osk-fade-enter-active,
.osk-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.osk-fade-enter,
.osk-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
